import { useState, useRef, useEffect, type FormEvent } from 'react'
import { router } from '@inertiajs/react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { Question, ScalePresetRecord } from '@/types'
import { SCALE_PRESETS } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
}

function questionTypeLabel(q: Question): string {
  if (q.question_type === 'scale') {
    if (q.scale_labels) return `Custom (1–${q.scale_points})`
    if (q.scale_preset && SCALE_PRESETS[q.scale_preset]) return SCALE_PRESETS[q.scale_preset].label
    return `Scale (1–${q.resolved_points})`
  }
  if (q.question_type === 'open_text') return 'Open Text'
  if (q.question_type === 'single_choice') return 'Single Choice'
  return q.question_type
}

interface QuestionBuilderProps {
  surveyId: number
  questions: Question[]
  locked: boolean
  /** Base path for question CRUD endpoints. Defaults to `/surveys/${surveyId}` */
  basePath?: string
  /** The account's saved custom scales, offered alongside the built-in presets. */
  scalePresets?: ScalePresetRecord[]
}

export default function QuestionBuilder({ surveyId, questions, locked, basePath, scalePresets }: QuestionBuilderProps) {
  const ap = useAccountPath()
  const questionsPath = basePath ?? ap(`/surveys/${surveyId}`)
  const [items, setItems] = useState<Question[]>(questions)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [accountPresets, setAccountPresets] = useState<ScalePresetRecord[]>(scalePresets ?? [])

  // Re-sync local state when the server delivers fresh props — done during
  // render (react.dev "adjusting state when props change"), not in an effect.
  const [prevQuestions, setPrevQuestions] = useState(questions)
  if (questions !== prevQuestions) {
    setPrevQuestions(questions)
    setItems(questions)
  }
  const [prevScalePresets, setPrevScalePresets] = useState(scalePresets)
  if (scalePresets !== prevScalePresets) {
    setPrevScalePresets(scalePresets)
    setAccountPresets(scalePresets ?? [])
  }

  // Saved scales are managed with plain JSON calls (not Inertia visits) so the
  // in-progress question form keeps its state.
  async function createAccountPreset(name: string, points: number, labels: string[]): Promise<ScalePresetRecord | string> {
    const response = await fetch(ap('/scale_presets'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
      body: JSON.stringify({ scale_preset: { name, points, labels } }),
    })
    if (response.status === 201) {
      const record: ScalePresetRecord = await response.json()
      setAccountPresets((current) => [...current, record].sort((a, b) => a.name.localeCompare(b.name)))
      return record
    }
    if (response.status === 422) {
      const body = await response.json().catch(() => ({ errors: [] }))
      return (body.errors ?? []).join(', ') || 'Could not save this scale.'
    }
    return 'Could not save this scale.'
  }

  async function deleteAccountPreset(id: number): Promise<void> {
    const response = await fetch(ap(`/scale_presets/${id}`), {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
    })
    if (response.ok) setAccountPresets((current) => current.filter((p) => p.id !== id))
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = items.findIndex((q) => q.id === active.id)
    const newIndex = items.findIndex((q) => q.id === over.id)

    const reordered = arrayMove(items, oldIndex, newIndex)
    setItems(reordered)

    const positions = reordered.map((q, i) => ({ id: q.id, position: i + 1 }))
    router.patch(`${questionsPath}/questions/reorder`, { positions }, {
      preserveScroll: true,
      onError: () => setItems(questions),
    })
  }

  function handleDelete(questionId: number) {
    if (!confirm('Remove this question?')) return
    router.delete(`${questionsPath}/questions/${questionId}`, {
      preserveScroll: true,
    })
  }

  return (
    <div className="card bg-base-100">
      <div className="card-body space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Questions
            <span className="badge badge-sm badge-ghost ms-2">{items.length}</span>
          </h2>
          {!locked && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setShowAddForm(true)}
            >
              <span className="iconify lucide--plus size-4" />
              Add question
            </button>
          )}
        </div>

        {locked && (
          <div className="alert alert-soft alert-info text-sm">
            <span className="iconify lucide--lock size-4" />
            Questions are locked because responses have already been collected.
          </div>
        )}

        {showAddForm && !locked && (
          <QuestionForm
            mode="add"
            questionsPath={questionsPath}
            onClose={() => setShowAddForm(false)}
            accountPresets={accountPresets}
            onCreatePreset={createAccountPreset}
            onDeletePreset={deleteAccountPreset}
          />
        )}

        {items.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={items.map((q) => q.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {items.map((question, index) => (
                  <SortableQuestionCard
                    key={question.id}
                    question={question}
                    index={index}
                    questionsPath={questionsPath}
                    locked={locked}
                    isEditing={editingId === question.id}
                    onStartEdit={() => setEditingId(question.id)}
                    onStopEdit={() => setEditingId(null)}
                    onDelete={() => handleDelete(question.id)}
                    accountPresets={accountPresets}
                    onCreatePreset={createAccountPreset}
                    onDeletePreset={deleteAccountPreset}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <p className="text-base-content/50 text-sm">
            No questions added yet. Click "Add question" to get started.
          </p>
        )}
      </div>
    </div>
  )
}

/* ----- Sortable Question Card ----- */

interface SortableQuestionCardProps {
  question: Question
  index: number
  questionsPath: string
  locked: boolean
  isEditing: boolean
  onStartEdit: () => void
  onStopEdit: () => void
  onDelete: () => void
  accountPresets: ScalePresetRecord[]
  onCreatePreset: (name: string, points: number, labels: string[]) => Promise<ScalePresetRecord | string>
  onDeletePreset: (id: number) => Promise<void>
}

function SortableQuestionCard({
  question, index, questionsPath, locked, isEditing, onStartEdit, onStopEdit, onDelete,
  accountPresets, onCreatePreset, onDeletePreset,
}: SortableQuestionCardProps) {
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging,
  } = useSortable({ id: question.id, disabled: locked })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  if (isEditing && !locked) {
    return (
      <div ref={setNodeRef} style={style}>
        <QuestionForm
          mode="edit"
          question={question}
          questionsPath={questionsPath}
          onClose={onStopEdit}
          accountPresets={accountPresets}
          onCreatePreset={onCreatePreset}
          onDeletePreset={onDeletePreset}
        />
      </div>
    )
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 rounded-lg border border-base-300 bg-base-100 p-3 text-sm group"
    >
      {!locked && (
        <button
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          type="button"
          className="cursor-grab active:cursor-grabbing text-base-content/30 hover:text-base-content/60 mt-0.5 shrink-0 touch-none"
          title="Drag to reorder"
        >
          <span className="iconify lucide--grip-vertical size-4" />
        </button>
      )}

      <span className="text-base-content/40 tabular-nums shrink-0 mt-0.5 w-5 text-end">
        {index + 1}.
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <span className="flex-1">
            {question.question_text}
            {question.required && (
              <span className="text-error ms-1" title="Required">*</span>
            )}
          </span>
          {question.retrospective && (
            <span className="badge badge-soft badge-info badge-sm shrink-0" title="Asks for a before and an after rating">
              Before / After
            </span>
          )}
          <span className="badge badge-soft badge-sm shrink-0">
            {questionTypeLabel(question)}
          </span>
        </div>

        {question.question_type === 'single_choice' && question.options && question.options.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {question.options.map((opt, i) => (
              <span key={i} className="badge badge-outline badge-xs">{opt}</span>
            ))}
          </div>
        )}

        {question.question_type === 'scale' && question.resolved_labels && (
          <div className="mt-1.5 text-xs text-base-content/50">
            {question.resolved_labels.filter(Boolean).join(' → ')}
          </div>
        )}
      </div>

      {!locked && (
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button type="button" className="btn btn-square btn-ghost btn-sm" title="Edit question" onClick={onStartEdit}>
            <span className="iconify lucide--pencil size-4" />
          </button>
          <button type="button" className="btn btn-square btn-ghost btn-sm text-error" title="Delete question" onClick={onDelete}>
            <span className="iconify lucide--trash-2 size-4" />
          </button>
        </div>
      )}
    </div>
  )
}

/* ----- Unified Question Form (Add + Edit) ----- */

interface QuestionFormProps {
  mode: 'add' | 'edit'
  question?: Question
  questionsPath: string
  onClose: () => void
  accountPresets: ScalePresetRecord[]
  onCreatePreset: (name: string, points: number, labels: string[]) => Promise<ScalePresetRecord | string>
  onDeletePreset: (id: number) => Promise<void>
}

function QuestionForm({ mode, question, questionsPath, onClose, accountPresets, onCreatePreset, onDeletePreset }: QuestionFormProps) {
  const [questionText, setQuestionText] = useState(question?.question_text ?? '')
  const [questionType, setQuestionType] = useState(question?.question_type ?? 'scale')
  const [required, setRequired] = useState(question?.required ?? true)
  const [retrospective, setRetrospective] = useState(question?.retrospective ?? false)
  const [options, setOptions] = useState<string[]>(question?.options ?? ['Option 1', 'Option 2'])

  // Scale state
  const [scalePreset, setScalePreset] = useState<string | null>(question?.scale_preset ?? 'agreement_5')
  const [scaleLabels, setScaleLabels] = useState<string[]>(question?.scale_labels ?? [])
  const [scalePoints, setScalePoints] = useState(question?.scale_points ?? 5)
  const [isCustomScale, setIsCustomScale] = useState(!!question?.scale_labels)

  const [submitting, setSubmitting] = useState(false)

  // Save-as-preset state (custom scale editor)
  const [presetName, setPresetName] = useState('')
  const [savingPreset, setSavingPreset] = useState(false)
  const [presetNotice, setPresetNotice] = useState<{ kind: 'saved' | 'error'; text: string } | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  function handlePresetChange(presetKey: string) {
    // The account's saved scales apply as a snapshot: labels are copied into the
    // question as a custom scale, so later preset edits never touch it.
    if (presetKey.startsWith('account:')) {
      const saved = accountPresets.find((p) => `account:${p.id}` === presetKey)
      if (!saved) return
      setScalePoints(saved.points)
      setScaleLabels(saved.labels.map((l) => l ?? ''))
      setScalePreset(null)
      setIsCustomScale(true)
      return
    }
    const preset = SCALE_PRESETS[presetKey]
    if (!preset) return
    setScalePreset(presetKey)
    setScalePoints(preset.points)
    setScaleLabels([])
    setIsCustomScale(false)
  }

  async function handleSavePreset() {
    const name = presetName.trim()
    if (!name || savingPreset) return
    setSavingPreset(true)
    setPresetNotice(null)
    const result = await onCreatePreset(name, scalePoints, scaleLabels)
    setSavingPreset(false)
    if (typeof result === 'string') {
      setPresetNotice({ kind: 'error', text: result })
    } else {
      setPresetName('')
      setPresetNotice({ kind: 'saved', text: `Saved "${result.name}" — it's now in the scale type list.` })
    }
  }

  function handleCustomize() {
    // Copy current preset labels into editable custom fields
    const preset = scalePreset ? SCALE_PRESETS[scalePreset] : null
    const labels = preset?.labels ?? Array.from({ length: scalePoints }, (_, i) => `${i + 1}`)
    setScaleLabels(labels.map((l) => l ?? ''))
    setScalePreset(null)
    setIsCustomScale(true)
  }

  function handleResetToPreset() {
    setScalePreset('agreement_5')
    setScalePoints(5)
    setScaleLabels([])
    setIsCustomScale(false)
  }

  function handleScalePointsChange(newPoints: number) {
    setScalePoints(newPoints)
    // Resize labels array
    const current = [...scaleLabels]
    if (newPoints > current.length) {
      setScaleLabels([...current, ...Array(newPoints - current.length).fill('')])
    } else {
      setScaleLabels(current.slice(0, newPoints))
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!questionText.trim()) return
    setSubmitting(true)

    const data = {
      question: {
        question_text: questionText.trim(),
        question_type: questionType,
        required,
        ...(questionType === 'single_choice' ? { options: options.filter((o) => o.trim()) } : {}),
        // Always sent, never only inside the scale spread: switching a retrospective
        // question to another type must carry `false`, or the stored flag survives and
        // the save is rejected with the toggle already hidden.
        retrospective: questionType === 'scale' ? retrospective : false,
        ...(questionType === 'scale' ? {
          scale_preset: isCustomScale ? null : scalePreset,
          scale_labels: isCustomScale ? scaleLabels : null,
          scale_points: isCustomScale ? scalePoints : (scalePreset ? SCALE_PRESETS[scalePreset]?.points : scalePoints),
        } : {}),
      },
    }

    const url = mode === 'add'
      ? `${questionsPath}/questions`
      : `${questionsPath}/questions/${question!.id}`

    const method = mode === 'add' ? 'post' : 'patch'

    router[method](url, data, {
      preserveScroll: true,
      onSuccess: () => { setSubmitting(false); onClose() },
      onError: () => setSubmitting(false),
    })
  }

  const currentLabels = isCustomScale
    ? scaleLabels
    : (scalePreset ? SCALE_PRESETS[scalePreset]?.labels ?? [] : [])

  return (
    <form onSubmit={handleSubmit} className={`rounded-lg border p-4 space-y-3 ${mode === 'add' ? 'border-primary/30 bg-primary/5' : 'border-info/30 bg-info/5'}`}>
      <h3 className="font-medium text-sm">{mode === 'add' ? 'Add new question' : 'Edit question'}</h3>

      <fieldset className="fieldset">
        <legend className="fieldset-legend">Question text</legend>
        <input
          ref={inputRef}
          type="text"
          className="input w-full"
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          placeholder="Enter your question..."
          required
        />
      </fieldset>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Question type</legend>
          <select
            className="select w-full"
            value={questionType}
            onChange={(e) => setQuestionType(e.target.value as Question['question_type'])}
          >
            <option value="scale">Scale</option>
            <option value="open_text">Open Text</option>
            <option value="single_choice">Single Choice</option>
          </select>
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Required</legend>
          <label className="label cursor-pointer justify-start gap-2">
            <input
              type="checkbox"
              className="toggle toggle-sm toggle-primary"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
            />
            <span className="text-sm">{required ? 'Required' : 'Optional'}</span>
          </label>
        </fieldset>
      </div>

      {/* Scale preset picker */}
      {questionType === 'scale' && !isCustomScale && (
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Scale type</legend>
          <select
            className="select w-full"
            value={scalePreset ?? ''}
            onChange={(e) => handlePresetChange(e.target.value)}
          >
            <optgroup label="5-point scales">
              {Object.entries(SCALE_PRESETS).filter(([, p]) => p.points === 5).map(([key, preset]) => (
                <option key={key} value={key}>{preset.label}</option>
              ))}
            </optgroup>
            <optgroup label="7-point scales">
              {Object.entries(SCALE_PRESETS).filter(([, p]) => p.points === 7).map(([key, preset]) => (
                <option key={key} value={key}>{preset.label}</option>
              ))}
            </optgroup>
            <optgroup label="10-point scales">
              {Object.entries(SCALE_PRESETS).filter(([, p]) => p.points === 10).map(([key, preset]) => (
                <option key={key} value={key}>{preset.label}</option>
              ))}
            </optgroup>
            {accountPresets.length > 0 && (
              <optgroup label="Your saved scales">
                {accountPresets.map((preset) => (
                  <option key={preset.id} value={`account:${preset.id}`}>{preset.name} (1–{preset.points})</option>
                ))}
              </optgroup>
            )}
          </select>

          {accountPresets.length > 0 && (
            <details className="mt-2">
              <summary className="text-xs text-base-content/50 cursor-pointer select-none">Manage saved scales</summary>
              <div className="mt-1 space-y-0.5">
                {accountPresets.map((preset) => (
                  <div key={preset.id} className="flex items-center gap-1 text-xs text-base-content/60">
                    <button
                      type="button"
                      className="btn btn-square btn-ghost btn-xs text-error"
                      title={`Delete "${preset.name}"`}
                      onClick={() => { if (confirm(`Delete the saved scale "${preset.name}"? Questions already using it keep their labels.`)) void onDeletePreset(preset.id) }}
                    >
                      <span className="iconify lucide--trash-2 size-3" />
                    </button>
                    <span>{preset.name} (1–{preset.points})</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Preview preset labels */}
          {currentLabels.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {currentLabels.map((label, i) => (
                <div key={i} className="text-xs text-base-content/50">
                  <span className="font-medium">{i + 1}</span>
                  {label && <span> — {label}</span>}
                </div>
              ))}
            </div>
          )}

          <button type="button" className="btn btn-ghost btn-xs mt-2" onClick={handleCustomize}>
            <span className="iconify lucide--pencil size-3" />
            Customize labels
          </button>
        </fieldset>
      )}

      {/* Custom scale labels editor */}
      {questionType === 'scale' && isCustomScale && (
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Custom scale labels</legend>

          <div className="mb-2">
            <label className="text-xs text-base-content/60">Number of points</label>
            <select
              className="select select-sm w-20 ms-2"
              value={scalePoints}
              onChange={(e) => handleScalePointsChange(Number(e.target.value))}
            >
              {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            {scaleLabels.map((label, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs font-medium text-base-content/50 w-5 text-end shrink-0">{i + 1}</span>
                <input
                  type="text"
                  className="input input-sm flex-1"
                  value={label}
                  onChange={(e) => {
                    const updated = [...scaleLabels]
                    updated[i] = e.target.value
                    setScaleLabels(updated)
                  }}
                  placeholder={`Label for ${i + 1} (optional)`}
                />
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-2">
            <button type="button" className="btn btn-ghost btn-xs" onClick={handleResetToPreset}>
              <span className="iconify lucide--undo size-3" />
              Reset to preset
            </button>

            {/* Save this scale as an account preset for reuse on other questions */}
            <div className="flex items-center gap-1">
              <input
                type="text"
                className="input input-xs w-36"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="Scale name…"
                maxLength={60}
              />
              <button
                type="button"
                className="btn btn-soft btn-primary btn-xs"
                disabled={savingPreset || !presetName.trim()}
                onClick={() => void handleSavePreset()}
              >
                {savingPreset && <span className="loading loading-spinner loading-xs" />}
                Save scale
              </button>
            </div>
          </div>

          {presetNotice && (
            <p className={`text-xs mt-1 text-end ${presetNotice.kind === 'saved' ? 'text-success' : 'text-error'}`}>
              {presetNotice.text}
            </p>
          )}
        </fieldset>
      )}

      {/* Retrospective is a property of the QUESTION, not the survey: one item can
          ask before/after while its siblings stay a single rating. Scale-only —
          there is nothing to compare on open text or single choice. */}
      {questionType === 'scale' && (
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Before and after</legend>
          <label className="label cursor-pointer justify-start gap-2">
            <input
              type="checkbox"
              className="toggle toggle-sm toggle-primary"
              checked={retrospective}
              onChange={(e) => setRetrospective(e.target.checked)}
            />
            <span className="text-sm">{retrospective ? 'Asks before and after' : 'Single rating'}</span>
          </label>
          <p className="label whitespace-normal">
            Respondents rate this question twice in one sitting: where they were before the program,
            and where they are now. Other scale questions in this survey are unaffected.
          </p>
        </fieldset>
      )}

      {questionType === 'single_choice' && (
        <OptionsEditor options={options} onChange={setOptions} />
      )}

      <div className="flex justify-end gap-2">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={submitting || !questionText.trim()}>
          {submitting && <span className="loading loading-spinner loading-sm" />}
          {mode === 'add' ? 'Add question' : 'Save changes'}
        </button>
      </div>
    </form>
  )
}

/* ----- Options Editor (for single_choice) ----- */

interface OptionsEditorProps {
  options: string[]
  onChange: (options: string[]) => void
}

function OptionsEditor({ options, onChange }: OptionsEditorProps) {
  function updateOption(index: number, value: string) {
    const updated = [...options]
    updated[index] = value
    onChange(updated)
  }

  function removeOption(index: number) {
    if (options.length <= 2) return
    onChange(options.filter((_, i) => i !== index))
  }

  function addOption() {
    onChange([...options, `Option ${options.length + 1}`])
  }

  return (
    <fieldset className="fieldset">
      <legend className="fieldset-legend">Answer options</legend>
      <div className="space-y-2">
        {options.map((opt, i) => (
          <div key={i} className="flex gap-2">
            <input
              type="text"
              className="input input-sm flex-1"
              value={opt}
              onChange={(e) => updateOption(i, e.target.value)}
              placeholder={`Option ${i + 1}`}
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm btn-square"
              onClick={() => removeOption(i)}
              disabled={options.length <= 2}
              title="Remove option"
            >
              <span className="iconify lucide--x size-4" />
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-ghost btn-sm" onClick={addOption}>
          <span className="iconify lucide--plus size-4" />
          Add option
        </button>
      </div>
    </fieldset>
  )
}
